import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface BaseEvent {
  id: string;
  title: string;
  date: string;
  type: string;
}

interface SchoolEvent extends BaseEvent {
  time: string;
  description: string;
  location: string;
  participants: string;
}

interface Holiday extends BaseEvent {
  description: string;
  isNational: boolean;
}

interface ChildEvent extends BaseEvent {
  time: string;
  subject: string;
  teacher: string;
  childId: string;
}

type EventType = SchoolEvent | Holiday | ChildEvent;

const EventsCalendar = () => {
  const routes = all_routes;

  const [children] = useState([
    { id: "1", name: "Emma Johnson", grade: "10th", class: "10-A" },
    { id: "2", name: "James Johnson", grade: "8th", class: "8-B" },
  ]);

  const [selectedChildId, setSelectedChildId] = useState("1");
  const [viewType, setViewType] = useState<"daily" | "weekly">("weekly");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const selectedChild = children.find((child) => child.id === selectedChildId);

  // Mock events data
  const eventsData = {
    schoolEvents: [
      {
        id: "1",
        title: "Science Fair",
        date: "2024-08-15",
        time: "9:00 AM - 3:00 PM",
        type: "academic",
        description: "Annual Science Exhibition",
        location: "Main Auditorium",
        participants: "All Grades",
      },
      {
        id: "2",
        title: "Parent-Teacher Meeting",
        date: "2024-08-20",
        time: "3:00 PM - 6:00 PM",
        type: "meeting",
        description: "Individual meetings with teachers",
        location: "Classrooms",
        participants: "Parents",
      },
      {
        id: "3",
        title: "Sports Day",
        date: "2024-08-25",
        time: "8:00 AM - 4:00 PM",
        type: "sports",
        description: "Annual Athletics Competition",
        location: "School Grounds",
        participants: "All Grades",
      },
      {
        id: "8",
        title: "Art Exhibition",
        date: "2024-08-12",
        time: "10:00 AM - 2:00 PM",
        type: "academic",
        description: "Student Art Display",
        location: "Art Gallery",
        participants: "All Grades",
      },
      {
        id: "9",
        title: "Music Concert",
        date: "2024-08-14",
        time: "7:00 PM - 9:00 PM",
        type: "cultural",
        description: "School Orchestra Performance",
        location: "Main Hall",
        participants: "All Welcome",
      },
    ],
    holidays: [
      {
        id: "4",
        title: "Independence Day",
        date: "2024-08-15",
        type: "holiday",
        description: "National Holiday - School Closed",
        isNational: true,
      },
      {
        id: "5",
        title: "School Founders Day",
        date: "2024-08-28",
        type: "holiday",
        description: "School Holiday",
        isNational: false,
      },
    ],
    childEvents: [
      {
        id: "6",
        title: "Mathematics Test",
        date: "2024-08-12",
        time: "10:00 AM",
        type: "exam",
        subject: "Mathematics",
        teacher: "Mrs. Smith",
        childId: "1",
      },
      {
        id: "7",
        title: "English Presentation",
        date: "2024-08-18",
        time: "2:00 PM",
        type: "assignment",
        subject: "English",
        teacher: "Mr. Brown",
        childId: "1",
      },
      {
        id: "10",
        title: "Science Project Due",
        date: "2024-08-13",
        time: "11:00 AM",
        type: "assignment",
        subject: "Science",
        teacher: "Dr. Wilson",
        childId: "1",
      },
    ],
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case "academic":
        return "primary";
      case "sports":
        return "success";
      case "meeting":
        return "warning";
      case "holiday":
        return "danger";
      case "exam":
        return "info";
      case "assignment":
        return "secondary";
      case "cultural":
        return "purple";
      default:
        return "light";
    }
  };

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case "academic":
        return "ti-school";
      case "sports":
        return "ti-ball-football";
      case "meeting":
        return "ti-users";
      case "holiday":
        return "ti-calendar-off";
      case "exam":
        return "ti-clipboard-check";
      case "assignment":
        return "ti-file-text";
      case "cultural":
        return "ti-music";
      default:
        return "ti-calendar-event";
    }
  };

  // Helper function to get event description
  const getEventDescription = (event: EventType): string => {
    if ("description" in event) {
      return event.description;
    }
    if ("subject" in event) {
      return `${event.subject} - ${event.teacher}`;
    }
    return "";
  };

  // Helper function to get event time
  const getEventTime = (event: EventType): string | undefined => {
    if ("time" in event) {
      return event.time;
    }
    return undefined;
  };

  // Helper function to get event location
  const getEventLocation = (event: EventType): string | undefined => {
    if ("location" in event) {
      return event.location;
    }
    return undefined;
  };

  const allEvents = [
    ...eventsData.schoolEvents,
    ...eventsData.holidays,
    ...eventsData.childEvents.filter((e) => e.childId === selectedChildId),
  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const getWeekDates = (date: string) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    const sunday = new Date(d.setDate(diff));

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const weekDay = new Date(sunday);
      weekDay.setDate(sunday.getDate() + i);
      weekDates.push(weekDay.toISOString().split("T")[0]);
    }
    return weekDates;
  };

  const getCurrentEvents = () => {
    if (viewType === "daily") {
      return allEvents.filter((event) => event.date === selectedDate);
    } else {
      const weekDates = getWeekDates(selectedDate);
      return allEvents.filter((event) => weekDates.includes(event.date));
    }
  };

  const formatDateHeader = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const groupEventsByDate = () => {
    const currentEvents = getCurrentEvents();
    return currentEvents.reduce((groups, event) => {
      if (!groups[event.date]) groups[event.date] = [];
      groups[event.date].push(event);
      return groups;
    }, {} as Record<string, EventType[]>);
  };

  const navigateDate = (direction: "prev" | "next") => {
    const currentDate = new Date(selectedDate);
    const days =
      viewType === "weekly"
        ? direction === "next"
          ? 7
          : -7
        : direction === "next"
        ? 1
        : -1;
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split("T")[0]);
  };

  const renderDailyView = () => {
    const dayEvents = getCurrentEvents();

    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            Events for {formatDateHeader(selectedDate)}
          </h5>
        </div>
        <div className="card-body">
          {dayEvents.length > 0 ? (
            <div className="events-timeline">
              {dayEvents.map((event) => {
                const description = getEventDescription(event);
                const time = getEventTime(event);
                const location = getEventLocation(event);

                return (
                  <div
                    key={event.id}
                    className="row align-items-center p-3 mb-3 border rounded"
                  >
                    <div className="col-md-2">
                      <div className="text-center">
                        <div
                          className={`avatar avatar-md bg-${getEventTypeColor(
                            event.type
                          )} mb-2`}
                        >
                          <i
                            className={`ti ${getEventTypeIcon(
                              event.type
                            )} text-white`}
                          ></i>
                        </div>
                        {time && (
                          <small className="text-muted d-block">{time}</small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="event-info">
                        <h6 className="mb-1">{event.title}</h6>
                        {description && (
                          <p className="text-muted mb-1">{description}</p>
                        )}
                        {location && (
                          <small className="text-muted">
                            <i className="ti ti-map-pin me-1"></i>
                            {location}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-2">
                      <span
                        className={`badge bg-${getEventTypeColor(event.type)}`}
                      >
                        {event.type}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                <i className="ti ti-calendar-x fs-1 text-secondary"></i>
              </div>
              <h5>No events scheduled</h5>
              <p className="text-muted">
                No events found for the selected date.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWeeklyView = () => {
    const groupedEvents = groupEventsByDate();
    const weekDates = getWeekDates(selectedDate);
    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    return (
      <div className="row">
        {weekDates.map((date, index) => {
          const dayEvents = groupedEvents[date] || [];
          return (
            <div key={date} className="col-md-6 col-lg-4 mb-4">
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <h6 className="mb-0">{dayNames[index]}</h6>
                  <small className="opacity-75">
                    {new Date(date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </small>
                </div>
                <div className="card-body p-2">
                  {dayEvents.length > 0 ? (
                    dayEvents.map((event) => {
                      const time = getEventTime(event);
                      return (
                        <div key={event.id} className="mb-2 p-2 border rounded">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span
                              className={`badge bg-${getEventTypeColor(
                                event.type
                              )}`}
                            >
                              {event.title}
                            </span>
                            {time && (
                              <small className="text-muted">{time}</small>
                            )}
                          </div>
                          <small className="text-muted">{event.type}</small>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-3 text-muted">
                      <i className="ti ti-calendar-off mb-2 d-block"></i>
                      <small>No events</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <div className="d-flex align-items-center">
                <Link
                  to={routes.parentMenu}
                  className="btn btn-outline-secondary btn-sm me-3"
                >
                  <i className="ti ti-arrow-left"></i>
                </Link>
                <div>
                  <h3 className="page-title mb-1">Events Calendar</h3>
                  <p className="text-muted mb-0">School events & holidays</p>
                </div>
              </div>
            </div>
            <div className="col-auto">
              {children.length > 1 && (
                <div className="dropdown">
                  <button
                    className="btn btn-outline-primary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-user me-2"></i>
                    {selectedChild?.name}
                  </button>
                  <ul className="dropdown-menu">
                    {children.map((child) => (
                      <li key={child.id}>
                        <button
                          className={`dropdown-item ${
                            selectedChildId === child.id ? "active" : ""
                          }`}
                          onClick={() => setSelectedChildId(child.id)}
                        >
                          {child.name} - {child.grade} {child.class}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* View Controls */}
          <div className="row mb-4">
            <div className="col-md-3">
              <label className="form-label">Select Date</label>
              <input
                type="date"
                className="form-control"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">View Type</label>
              <div className="btn-group w-100">
                <button
                  className={`btn ${
                    viewType === "daily" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setViewType("daily")}
                >
                  Daily
                </button>
                <button
                  className={`btn ${
                    viewType === "weekly"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setViewType("weekly")}
                >
                  Weekly
                </button>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Navigation</label>
              <div className="btn-group w-100">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigateDate("prev")}
                >
                  <i className="ti ti-chevron-left"></i> Previous
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigateDate("next")}
                >
                  Next <i className="ti ti-chevron-right"></i>
                </button>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">&nbsp;</label>
              <button
                className="btn btn-outline-secondary w-100 d-block"
                onClick={goToToday}
              >
                <i className="ti ti-calendar-today me-1"></i>Today
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-md bg-primary mb-2">
                    <i className="ti ti-calendar-event text-white"></i>
                  </div>
                  <h5 className="text-primary">
                    {eventsData.schoolEvents.length}
                  </h5>
                  <p className="mb-0">School Events</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-danger-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-md bg-danger mb-2">
                    <i className="ti ti-calendar-off text-white"></i>
                  </div>
                  <h5 className="text-danger">{eventsData.holidays.length}</h5>
                  <p className="mb-0">Holidays</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-info-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-md bg-info mb-2">
                    <i className="ti ti-clipboard-check text-white"></i>
                  </div>
                  <h5 className="text-info">
                    {
                      eventsData.childEvents.filter(
                        (e) => e.childId === selectedChildId
                      ).length
                    }
                  </h5>
                  <p className="mb-0">Child Events</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-md bg-success mb-2">
                    <i className="ti ti-check text-white"></i>
                  </div>
                  <h5 className="text-success">{getCurrentEvents().length}</h5>
                  <p className="mb-0">Current Period</p>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Content */}
          {viewType === "daily" ? renderDailyView() : renderWeeklyView()}
        </div>
      </div>
    </>
  );
};

export default EventsCalendar;
